import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock } from 'lucide-react';
import { motion } from 'framer-motion';

export default function AdminLogin() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(username, password);
      navigate('/admin');
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#ED2227] rounded-sm flex items-center justify-center mx-auto mb-6">
            <Lock className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-black text-white font-[Chivo] uppercase tracking-tight">
            {t('admin.login')}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('admin.username')}</Label>
            <Input
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-[#121212] border-zinc-800 text-white h-12 rounded-sm focus:border-[#ED2227] focus:ring-[#ED2227]"
              data-testid="admin-username-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('admin.password')}</Label>
            <Input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#121212] border-zinc-800 text-white h-12 rounded-sm focus:border-[#ED2227] focus:ring-[#ED2227]"
              data-testid="admin-password-input"
            />
          </div>

          {error && (
            <p className="text-[#ED2227] text-sm" data-testid="admin-login-error">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider h-12 text-sm"
            data-testid="admin-login-submit"
          >
            {loading ? '...' : t('admin.signIn')}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
